import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import '../lib/apiConfig'; // Initialize API config
import { UsersService } from '../app/api/services/UsersService';
import { CorporateEntityService } from '../app/api/services/CorporateEntityService';
import { LocationsService } from '../app/api/services/LocationsService';
import { CountriesService } from '../app/api/services/CountriesService';
import { IndustriesService } from '../app/api/services/IndustriesService';
import { ProductsService } from '../app/api/services/ProductsService';
import { ServicesService } from '../app/api/services/ServicesService';
import { BuyerRequirementsService } from '../app/api/services/BuyerRequirementsService';
import { withAuth, getAuthToken, logout } from '../lib/auth';
import { formatPhoneNumber } from '../lib/utils';
import { cn } from '../lib/utils';
import { AsYouType } from 'libphonenumber-js';

// Helper functions for role-based logic
const isBuyer = (userType, goal) => {
    return userType === 'BUYER' || goal === 'buying';
};

const isBrowser = (userType) => {
    return userType === 'BROWSER';
};

const canManageCompanies = (userType) => {
    return !isBrowser(userType);
};

const getBuyingFlag = (userType, goal) => {
    // Primary: use userType, fallback to goal for backward compatibility
    if (userType === 'BUYER') return true;
    if (userType === 'SUPPLIER') return false;
    return goal === 'buying';
};

function Profile() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // User data
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [userType, setUserType] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [userId, setUserId] = useState(null);

    // Companies and locations
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [industries, setIndustries] = useState([]);

    // Company editing
    const [editingCompanyId, setEditingCompanyId] = useState(null);
    const [companyFormData, setCompanyFormData] = useState({});
    const [savingCompany, setSavingCompany] = useState(false);
    const [isCreatingCompany, setIsCreatingCompany] = useState(false);
    const [newCompanyFormData, setNewCompanyFormData] = useState({});

    // Location editing
    const [editingLocationId, setEditingLocationId] = useState(null);
    const [locationFormData, setLocationFormData] = useState({});
    const [savingLocation, setSavingLocation] = useState(false);
    const [creatingLocationForCompanyId, setCreatingLocationForCompanyId] = useState(null);
    const [newLocationFormData, setNewLocationFormData] = useState({});

    // Products and Services editing
    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [editingProductsLocationId, setEditingProductsLocationId] = useState(null);
    const [editingServicesLocationId, setEditingServicesLocationId] = useState(null);
    const [goal, setGoal] = useState(''); // To determine buying flag

    // Buyer requirements
    const [buyerRequirements, setBuyerRequirements] = useState([]);
    const [loadingRequirements, setLoadingRequirements] = useState(false);
    const [savingRequirementId, setSavingRequirementId] = useState(null);

    // Active tab/section
    const [activeTab, setActiveTab] = useState('profile');

    // Load user data and companies on mount
    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Check authentication first
                const token = await getAuthToken();
                if (!token) {
                    setError('You are not authenticated. Please log in to view your profile.');
                    setTimeout(() => navigate('/signup'), 2000);
                    return;
                }

                // Get userId from localStorage (same as Onboarding page)
                const storedUserId = parseInt(localStorage.getItem('Id') || '0', 10);
                if (!storedUserId) {
                    setError('User ID not found. Please log in again.');
                    setTimeout(() => navigate('/signup'), 2000);
                    return;
                }
                setUserId(storedUserId);

                // Fetch user data
                const response = await withAuth(async () => {
                    return await UsersService.getApiV2UsersMe();
                });

                if (response.data) {
                    setFirstname(response.data.firstname || '');
                    setLastname(response.data.lastname || '');
                    setEmail(response.data.uemail || '');
                    setUserType(response.data.userType || '');
                }

                // Load companies for this user
                const companiesData = await loadCompanies(storedUserId);

                // Load countries for location editing
                await loadCountries();

                // Load industries for company editing
                await loadIndustries();

                // Load products and services
                await loadProducts();
                await loadServices();

                // Load buyer requirements for this user (and map to companies if available)
                await loadBuyerRequirements(storedUserId, companiesData || []);

                // Get goal from localStorage to determine buying flag
                const storedGoal = localStorage.getItem('goal') || '';
                setGoal(storedGoal);
            } catch (error) {
                console.error('Error loading user data:', error);
                setError('Failed to load profile data. Please try again.');
                if (error?.status === 401) {
                    await logout();
                    setTimeout(() => navigate('/signup'), 2000);
                }
            } finally {
                setLoading(false);
            }
        };

        loadUserData();
    }, [navigate]);

    // Redirect BROWSER users away from companies tab if they somehow access it
    useEffect(() => {
        if (isBrowser(userType) && activeTab === 'companies') {
            setActiveTab('profile');
        }
    }, [userType, activeTab]);

    const loadCompanies = async (managerId) => {
        setLoadingCompanies(true);
        try {
            const response = await withAuth(async () => {
                return await CorporateEntityService.getApiV2CorporateEntity(
                    undefined, // search
                    undefined, // industry
                    undefined, // size
                    managerId, // managerid - filter by this user
                    undefined, // activeflag
                    undefined, // publishStatus
                    undefined, // moq
                    undefined, // productOrService
                    undefined, // certificate
                    undefined, // country
                    undefined, // page
                    undefined, // pageSize
                    undefined, // limit
                    undefined, // offset
                    undefined, // sort
                    'asc' // order
                );
            });

            if (response.data) {
                setCompanies(response.data);
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error loading companies:', error);
            // Don't show error for companies if user has no companies yet
            // The API might return empty array or 404, both are acceptable
            if (error?.status && error.status !== 404) {
                setError('Failed to load companies. Please try again.');
            }
        } finally {
            setLoadingCompanies(false);
        }
    };

    const loadBuyerRequirements = async (userIdForRequirements, companiesList) => {
        setLoadingRequirements(true);
        try {
            // Fetch all buyer requirements for this user
            const response = await withAuth(async () => {
                return await BuyerRequirementsService.getApiV2BuyerRequirements(
                    undefined, // cid
                    userIdForRequirements, // userid
                    undefined, // category
                    undefined  // priority
                );
            });

            const requirementsData = Array.isArray(response?.data)
                ? response.data
                : response;

            const safeRequirements = Array.isArray(requirementsData) ? requirementsData : [];

            // Enrich with company info for display (if we know the companies)
            const enriched = safeRequirements.map((req) => {
                const company =
                    companiesList?.find(
                        (c) =>
                            c.id === req.cid ||
                            c.cid === req.cid
                    ) || null;

                return {
                    ...req,
                    companyId: company?.id || req.cid,
                    companyName:
                        company?.legalname ||
                        company?.legal_name ||
                        company?.companyname ||
                        'Company',
                };
            });

            setBuyerRequirements(enriched);
        } catch (error) {
            console.error('Error loading buyer requirements:', error);
            setBuyerRequirements([]);
        } finally {
            setLoadingRequirements(false);
        }
    };

    const loadCountries = async () => {
        try {
            const response = await CountriesService.getApiV2Countries();
            if (response.data) {
                setCountries(response.data);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    };

    const loadStates = async (countryCode) => {
        try {
            const response = await CountriesService.getApiV2CountriesStates(countryCode);
            if (response.data) {
                setStates(response.data);
            }
        } catch (error) {
            console.error('Error loading states:', error);
            setStates([]);
        }
    };

    const loadIndustries = async () => {
        try {
            const response = await IndustriesService.getApiV2Industries();
            if (response.data) {
                setIndustries(response.data);
            }
        } catch (error) {
            console.error('Error loading industries:', error);
        }
    };

    const getCleanPhoneNumber = (formattedPhone) => {
        return formattedPhone.replace(/\D/g, '');
    };

    const loadProducts = async () => {
        try {
            const response = await ProductsService.getApiV2Products();
            if (response.data) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    const loadServices = async () => {
        try {
            const response = await ServicesService.getApiV2Services();
            if (response.data) {
                setServices(response.data);
            }
        } catch (error) {
            console.error('Error loading services:', error);
        }
    };

    const handleEditLocation = (location) => {
        setEditingLocationId(location.id);
        setLocationFormData({
            address: location.address || '',
            city: location.city || '',
            state: location.state || '',
            postal_code: location.postal_code || '',
            country_code: location.country_code || '',
            businessfunction: location.businessfunction || '',
        });
        if (location.country_code) {
            loadStates(location.country_code);
        }
    };

    const handleCancelEditLocation = () => {
        setEditingLocationId(null);
        setLocationFormData({});
        setStates([]);
    };

    const handleStartCreateLocation = (companyId) => {
        setCreatingLocationForCompanyId(companyId);
        setNewLocationFormData({
            address: '',
            city: '',
            state: '',
            postal_code: '',
            country_code: '',
            businessfunction: '',
        });
        setStates([]);
    };

    const handleCancelCreateLocation = () => {
        setCreatingLocationForCompanyId(null);
        setNewLocationFormData({});
        setStates([]);
    };

    const handleCreateLocation = async (companyId) => {
        setSavingLocation(true);
        setError('');
        setSuccess('');

        try {
            // Validate required fields (matching onboarding flow)
            if (!newLocationFormData.address?.trim()) {
                setError('Please enter your address');
                setSavingLocation(false);
                return;
            }

            if (!newLocationFormData.city?.trim()) {
                setError('Please enter your city');
                setSavingLocation(false);
                return;
            }

            if (!newLocationFormData.country_code) {
                setError('Please select a country');
                setSavingLocation(false);
                return;
            }

            // Prepare create data
            const createData = {
                cid: companyId,
                managerid: userId,
                country_code: newLocationFormData.country_code.toUpperCase().trim(),
                address: newLocationFormData.address.trim(),
                city: newLocationFormData.city.trim(),
                state: newLocationFormData.state?.trim() || undefined,
                postal_code: newLocationFormData.postal_code?.trim() || undefined,
                businessfunction: newLocationFormData.businessfunction?.trim() || undefined,
            };

            // Use POST endpoint to create location
            await withAuth(async () => {
                return await LocationsService.postApiV2Locations(createData);
            });

            setSuccess('Location created successfully');

            // Reload companies to get updated data
            await loadCompanies(userId);

            // Close create mode
            setCreatingLocationForCompanyId(null);
            setNewLocationFormData({});
            setStates([]);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error creating location:', error);
            setError(error.message || 'Failed to create location. Please try again.');
        } finally {
            setSavingLocation(false);
        }
    };

    const handleEditCompany = (company) => {
        setEditingCompanyId(company.id);
        // Format phone number for display
        let formattedPhone = company.phone || '';
        if (formattedPhone && !formattedPhone.includes('(')) {
            const digits = formattedPhone.replace(/\D/g, '');
            if (digits) {
                const formatter = new AsYouType('US');
                formattedPhone = formatter.input(digits);
            }
        }
        setCompanyFormData({
            legal_name: company.legalname || '',
            website: company.website || '',
            phone: formattedPhone,
            size: company.size || '',
            industry: company.industry || '',
            MOQ: company.MOQ || '',
        });
    };

    const handleCancelEditCompany = () => {
        setEditingCompanyId(null);
        setCompanyFormData({});
    };

    const handleStartCreateCompany = () => {
        setIsCreatingCompany(true);
        setNewCompanyFormData({
            legal_name: '',
            website: '',
            phone: '',
            size: '',
            industry: '',
            MOQ: '',
        });
    };

    const handleCancelCreateCompany = () => {
        setIsCreatingCompany(false);
        setNewCompanyFormData({});
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        setSavingCompany(true);
        setError('');
        setSuccess('');

        try {
            // Validate required fields (matching onboarding flow)
            if (!newCompanyFormData.legal_name?.trim()) {
                setError('Please enter your company legal name');
                setSavingCompany(false);
                return;
            }

            if (!newCompanyFormData.website?.trim()) {
                setError('Please enter your company website');
                setSavingCompany(false);
                return;
            }

            // Validate and normalize website URL
            let normalizedWebsite = newCompanyFormData.website.trim();
            if (!normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
                normalizedWebsite = 'https://' + normalizedWebsite;
            }
            if (!normalizedWebsite.match(/^https?:\/\/.+/)) {
                setError('Please enter a valid website URL (must start with http:// or https://)');
                setSavingCompany(false);
                return;
            }

            // Validate phone number (required, at least 10 digits)
            const cleanPhone = getCleanPhoneNumber(newCompanyFormData.phone || '');
            if (!cleanPhone || cleanPhone.length < 10) {
                setError('Please enter a valid phone number (at least 10 digits)');
                setSavingCompany(false);
                return;
            }

            if (!newCompanyFormData.size) {
                setError('Please select company size');
                setSavingCompany(false);
                return;
            }

            if (!newCompanyFormData.industry) {
                setError('Please select an industry');
                setSavingCompany(false);
                return;
            }

            // Prepare create data
            const createData = {
                legal_name: newCompanyFormData.legal_name.trim(),
                website: normalizedWebsite,
                phone: cleanPhone,
                size: newCompanyFormData.size,
                industry: newCompanyFormData.industry,
                MOQ: newCompanyFormData.MOQ?.trim() || undefined,
                activeflag: true,
            };

            // Use POST endpoint to create corporate entity
            await withAuth(async () => {
                return await CorporateEntityService.postApiV2CorporateEntity(createData);
            });

            setSuccess('Company created successfully');

            // Reload companies to get updated data
            await loadCompanies(userId);

            // Close create mode
            setIsCreatingCompany(false);
            setNewCompanyFormData({});

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error creating company:', error);
            setError(error.message || 'Failed to create company. Please try again.');
        } finally {
            setSavingCompany(false);
        }
    };

    const handleSaveCompany = async (e, companyId) => {
        e.preventDefault();
        setSavingCompany(true);
        setError('');
        setSuccess('');

        try {
            // Validate required fields
            if (!companyFormData.legal_name?.trim()) {
                setError('Company legal name is required');
                setSavingCompany(false);
                return;
            }

            // Validate and normalize website URL
            let normalizedWebsite = companyFormData.website?.trim() || '';
            if (normalizedWebsite) {
                if (!normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
                    normalizedWebsite = 'https://' + normalizedWebsite;
                }
                if (!normalizedWebsite.match(/^https?:\/\/.+/)) {
                    setError('Please enter a valid website URL');
                    setSavingCompany(false);
                    return;
                }
            }

            // Validate phone number
            const cleanPhone = getCleanPhoneNumber(companyFormData.phone || '');
            if (companyFormData.phone && (!cleanPhone || cleanPhone.length < 10)) {
                setError('Please enter a valid phone number (at least 10 digits)');
                setSavingCompany(false);
                return;
            }

            if (!companyFormData.size) {
                setError('Company size is required');
                setSavingCompany(false);
                return;
            }

            if (!companyFormData.industry) {
                setError('Industry is required');
                setSavingCompany(false);
                return;
            }

            // Prepare update data
            const updateData = {
                legal_name: companyFormData.legal_name.trim(),
                website: normalizedWebsite || undefined,
                phone: cleanPhone || undefined,
                size: companyFormData.size,
                industry: companyFormData.industry,
                MOQ: companyFormData.MOQ?.trim() || undefined,
            };

            // Use PATCH endpoint to update corporate entity
            await withAuth(async () => {
                return await CorporateEntityService.patchApiV2CorporateEntity(companyId, updateData);
            });

            setSuccess('Company information updated successfully');

            // Reload companies to get updated data
            await loadCompanies(userId);

            // Close edit mode
            setEditingCompanyId(null);
            setCompanyFormData({});

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating company:', error);
            setError(error.message || 'Failed to update company. Please try again.');
        } finally {
            setSavingCompany(false);
        }
    };

    const handleEditProducts = (location) => {
        setEditingProductsLocationId(location.id);
    };

    const handleCancelEditProducts = () => {
        setEditingProductsLocationId(null);
    };

    const handleEditServices = (location) => {
        setEditingServicesLocationId(location.id);
    };

    const handleCancelEditServices = () => {
        setEditingServicesLocationId(null);
    };

    const toggleProductForLocation = async (e, locationId, productId, isSelected) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Optimistically update UI first
        setCompanies(prevCompanies => {
            return prevCompanies.map(company => {
                if (!company.location) return company;
                return {
                    ...company,
                    location: company.location.map(loc => {
                        if (loc.id !== locationId) return loc;
                        const currentProducts = loc.locationProducts || [];
                        if (isSelected) {
                            // Check if already exists
                            if (currentProducts.some(lp => lp.product?.id === productId)) {
                                return loc;
                            }
                            // Add product optimistically
                            const product = products.find(p => p.id === productId);
                            if (product) {
                                return {
                                    ...loc,
                                    locationProducts: [
                                        ...currentProducts,
                                        { product, buying: getBuyingFlag(userType, goal) }
                                    ]
                                };
                            }
                        } else {
                            // Remove product
                            return {
                                ...loc,
                                locationProducts: currentProducts.filter(
                                    lp => lp.product?.id !== productId
                                )
                            };
                        }
                        return loc;
                    })
                };
            });
        });

        try {
            const buying = getBuyingFlag(userType, goal);

            if (isSelected) {
                // Add product
                await withAuth(() =>
                    ProductsService.postApiV2ProductsLocation(locationId, {
                        products: [productId],
                        buying: buying,
                    })
                );
            } else {
                // Remove product
                await withAuth(() =>
                    ProductsService.deleteApiV2ProductsLocation(locationId, {
                        products: [productId],
                    })
                );
            }
        } catch (error) {
            console.error('Error toggling product:', error);
            setError(error.message || 'Failed to update product. Please try again.');
            // Reload on error to get correct state
            if (userId) {
                await loadCompanies(userId);
            }
        }
    };

    const toggleServiceForLocation = async (e, locationId, serviceId, isSelected) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Optimistically update UI first
        setCompanies(prevCompanies => {
            return prevCompanies.map(company => {
                if (!company.location) return company;
                return {
                    ...company,
                    location: company.location.map(loc => {
                        if (loc.id !== locationId) return loc;
                        const currentServices = loc.locationServices || [];
                        if (isSelected) {
                            // Check if already exists
                            if (currentServices.some(ls => ls.service?.id === serviceId)) {
                                return loc;
                            }
                            // Add service optimistically
                            const service = services.find(s => s.id === serviceId);
                            if (service) {
                                return {
                                    ...loc,
                                    locationServices: [
                                        ...currentServices,
                                        { service, buying: getBuyingFlag(userType, goal) }
                                    ]
                                };
                            }
                        } else {
                            // Remove service
                            return {
                                ...loc,
                                locationServices: currentServices.filter(
                                    ls => ls.service?.id !== serviceId
                                )
                            };
                        }
                        return loc;
                    })
                };
            });
        });

        try {
            const buying = getBuyingFlag(userType, goal);

            if (isSelected) {
                // Add service
                await withAuth(() =>
                    ServicesService.postApiV2ServicesLocation(locationId, {
                        services: [serviceId],
                        buying: buying,
                    })
                );
            } else {
                // Remove service
                await withAuth(() =>
                    ServicesService.deleteApiV2ServicesLocation(locationId, {
                        services: [serviceId],
                    })
                );
            }
        } catch (error) {
            console.error('Error toggling service:', error);
            setError(error.message || 'Failed to update service. Please try again.');
            // Reload on error to get correct state
            if (userId) {
                await loadCompanies(userId);
            }
        }
    };

    const handleSaveLocation = async (locationId, companyId) => {
        setSavingLocation(true);
        setError('');

        try {
            const updateData = {
                address: locationFormData.address?.trim() || undefined,
                city: locationFormData.city?.trim() || undefined,
                state: locationFormData.state?.trim() || undefined,
                postal_code: locationFormData.postal_code?.trim() || undefined,
                country_code: locationFormData.country_code?.toUpperCase().trim() || undefined,
                businessfunction: locationFormData.businessfunction?.trim() || undefined,
                cid: companyId,
            };

            const response = await withAuth(async () => {
                return await LocationsService.putApiV2Locations(locationId, updateData);
            });

            if (response) {
                setSuccess('Location updated successfully!');
                setTimeout(() => setSuccess(''), 3000);

                // Reload companies to get updated data
                if (userId) {
                    await loadCompanies(userId);
                }

                handleCancelEditLocation();
            }
        } catch (error) {
            console.error('Error updating location:', error);
            setError(error.message || 'Failed to update location. Please try again.');
        } finally {
            setSavingLocation(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        // Validation
        if (!firstname.trim()) {
            setError('First name is required');
            setSaving(false);
            return;
        }

        if (!lastname.trim()) {
            setError('Last name is required');
            setSaving(false);
            return;
        }

        if (!email.trim()) {
            setError('Email is required');
            setSaving(false);
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            setSaving(false);
            return;
        }

        try {
            const response = await withAuth(async () => {
                return await UsersService.patchApiV2UsersMe({
                    firstname: firstname.trim(),
                    lastname: lastname.trim(),
                    uemail: email.trim(),
                });
            });

            if (response) {
                setSuccess('Profile updated successfully!');
                setIsEditing(false);
                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setError(error.message || 'Failed to update profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleRequirementChange = (requirementId, field, value) => {
        setBuyerRequirements((prev) =>
            prev.map((req) =>
                req.id === requirementId
                    ? {
                        ...req,
                        [field]: value,
                    }
                    : req
            )
        );
    };

    const handleSaveRequirement = async (requirementId) => {
        const requirement = buyerRequirements.find((req) => req.id === requirementId);
        if (!requirement) {
            return;
        }

        if (!requirement.cid || !requirement.category) {
            setError('Missing company or category for this requirement. Please contact support.');
            return;
        }

        setSavingRequirementId(requirementId);
        setError('');
        setSuccess('');

        try {
            await withAuth(async () => {
                return await BuyerRequirementsService.putApiV2BuyerRequirements(requirementId, {
                    quantity: requirement.quantity || '',
                    timeline: requirement.timeline || '',
                    budget: requirement.budget || '',
                    geography: requirement.geography || '',
                    priority: requirement.priority || 'MEDIUM',
                    notes: requirement.notes || '',
                });
            });

            setSuccess('Buyer requirements updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating buyer requirement:', error);
            setError(error.message || 'Failed to update buyer requirement. Please try again.');
        } finally {
            setSavingRequirementId(null);
        }
    };

    const handleCancel = () => {
        // Reload original data
        const loadUserData = async () => {
            try {
                const response = await withAuth(async () => {
                    return await UsersService.getApiV2UsersMe();
                });

                if (response.data) {
                    setFirstname(response.data.firstname || '');
                    setLastname(response.data.lastname || '');
                    setEmail(response.data.uemail || '');
                    setUserType(response.data.userType || '');
                }
            } catch (error) {
                console.error('Error reloading user data:', error);
            }
        };

        loadUserData();
        setIsEditing(false);
        setError('');
        setSuccess('');
    };

    const handleDeleteProfile = async () => {
        if (!userId) {
            setError('Unable to delete profile: user ID is missing.');
            return;
        }

        const confirmed = window.confirm(
            'Are you sure you want to delete your profile? This will deactivate your account and remove your profile from the directory.'
        );
        if (!confirmed) return;

        setDeletingProfile(true);
        setError('');
        setSuccess('');

        try {
            await withAuth(() => UsersService.deleteApiV2Users(userId.toString()));
            setSuccess('Your profile has been deleted and your account has been deactivated.');
            await logout();
            navigate('/signup');
        } catch (err) {
            console.error('Error deleting profile:', err);
            const status = err?.status || err?.statusCode;
            if (status === 401 || status === 403) {
                await logout();
                setError('Your session has expired or you are not authorized. Please log in again.');
                navigate('/signup');
                return;
            }
            setError(
                err?.body?.message ||
                err?.message ||
                'Failed to delete profile. Please try again.'
            );
        } finally {
            setDeletingProfile(false);
        }
    };

    const hasRequirements = buyerRequirements && buyerRequirements.length > 0;
    const hasUnfilledRequirements =
        hasRequirements &&
        buyerRequirements.some((req) => {
            const hasQuantity = req.quantity && typeof req.quantity === 'string' && req.quantity.trim().length > 0;
            const hasTimeline = req.timeline && typeof req.timeline === 'string' && req.timeline.trim().length > 0;
            const hasBudget = req.budget && typeof req.budget === 'string' && req.budget.trim().length > 0;
            const hasGeography = req.geography && typeof req.geography === 'string' && req.geography.trim().length > 0;
            const hasNotes = req.notes && typeof req.notes === 'string' && req.notes.trim().length > 0;
            // A requirement is unfilled if ALL detail fields are empty/null
            return !(hasQuantity || hasTimeline || hasBudget || hasGeography || hasNotes);
        });

    const [deletingProfile, setDeletingProfile] = useState(false);

    if (loading) {
        return (
            <main className="App-main">
                <div className="flex justify-center items-center min-h-[60vh] flex-col gap-4">
                    <div className="text-xl text-muted-foreground">Loading profile...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="App-main">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-foreground mb-2">My Profile</h1>
                    <p className="text-muted-foreground">Manage your account information and view your companies</p>
                </div>

                {/* Messages */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                        {success}
                    </div>
                )}

                {/* Buyer Requirements Notification */}
                {isBuyer(userType, goal) &&
                    hasRequirements &&
                    hasUnfilledRequirements &&
                    !loadingRequirements &&
                    activeTab !== 'requirements' && (
                        <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-md">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold text-amber-900 mb-1">
                                        Complete your requirements to get better matches
                                    </div>
                                    <p className="text-sm text-amber-800">
                                        You have requirements without details yet. Please complete them in the{' '}
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('requirements')}
                                            className="text-amber-900 hover:text-amber-700 font-semibold underline"
                                        >
                                            Requirements
                                        </button>{' '}
                                        tab so we can better understand what you&apos;re looking for.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Tab Navigation */}
                <div className="mb-6 border-b border-border">
                    <nav className="flex space-x-8" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`
                                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                ${activeTab === 'profile'
                                    ? 'border-brand-orange text-brand-orange'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                }
                            `}
                        >
                            Personal Information
                        </button>
                        {canManageCompanies(userType) && (
                            <button
                                onClick={() => setActiveTab('companies')}
                                className={`
                                    py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                    ${activeTab === 'companies'
                                        ? 'border-brand-orange text-brand-orange'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                    }
                                `}
                            >
                                My Companies
                                {companies.length > 0 && (
                                    <span className="ml-2 py-0.5 px-2 text-xs bg-muted text-muted-foreground rounded-full">
                                        {companies.length}
                                    </span>
                                )}
                            </button>
                        )}
                        {isBuyer(userType, goal) && (
                            <button
                                onClick={() => setActiveTab('requirements')}
                                className={`
                                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                ${activeTab === 'requirements'
                                        ? 'border-brand-orange text-brand-orange'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                    }
                            `}
                            >
                                Requirements
                                {buyerRequirements.length > 0 && (
                                    <span className="ml-2 py-0.5 px-2 text-xs bg-muted text-muted-foreground rounded-full">
                                        {buyerRequirements.length}
                                    </span>
                                )}
                            </button>
                        )}
                    </nav>
                </div>

                {/* Profile Information Card */}
                {activeTab === 'profile' && (
                    <div className="bg-card rounded-lg shadow-sm border border-border mb-8">
                        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
                            {!isEditing && (
                                <Button
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Edit
                                </Button>
                            )}
                        </div>
                        <form onSubmit={handleSave} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        First Name
                                    </label>
                                    <Input
                                        type="text"
                                        value={firstname}
                                        onChange={(e) => setFirstname(e.target.value)}
                                        disabled={!isEditing}
                                        required
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Last Name
                                    </label>
                                    <Input
                                        type="text"
                                        value={lastname}
                                        onChange={(e) => setLastname(e.target.value)}
                                        disabled={!isEditing}
                                        required
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Email
                                    </label>
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={!isEditing}
                                        required
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        User Type
                                    </label>
                                    <Input
                                        type="text"
                                        value={userType || 'Not set'}
                                        disabled
                                        className="w-full bg-muted cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {isEditing && (
                                <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
                                    <Button
                                        type="button"
                                        onClick={handleCancel}
                                        variant="outline"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </div>
                            )}
                        </form>
                    </div>
                )}

                {/* My Companies Section */}
                {activeTab === 'companies' && canManageCompanies(userType) && (
                    <div>
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-foreground mb-2">My Companies</h2>
                                <p className="text-muted-foreground">Companies you manage and their locations</p>
                            </div>
                            {!isCreatingCompany && (
                                <Button
                                    type="button"
                                    onClick={handleStartCreateCompany}
                                    variant="default"
                                >
                                    + Add Company
                                </Button>
                            )}
                        </div>

                        {loadingCompanies ? (
                            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                                <div className="text-muted-foreground">Loading companies...</div>
                            </div>
                        ) : isCreatingCompany ? (
                            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden mb-6">
                                <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                                    <h3 className="text-xl font-semibold text-foreground mb-4">
                                        Create New Company
                                    </h3>
                                    <form onSubmit={handleCreateCompany} className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Legal Name *
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={newCompanyFormData.legal_name || ''}
                                                    onChange={(e) => setNewCompanyFormData({ ...newCompanyFormData, legal_name: e.target.value })}
                                                    required
                                                    className="w-full"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Website URL *
                                                </label>
                                                <Input
                                                    type="url"
                                                    value={newCompanyFormData.website || ''}
                                                    onChange={(e) => setNewCompanyFormData({ ...newCompanyFormData, website: e.target.value })}
                                                    onBlur={(e) => {
                                                        let value = e.target.value.trim();
                                                        // Auto-fill https:// if missing protocol
                                                        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                                                            setNewCompanyFormData({ ...newCompanyFormData, website: 'https://' + value });
                                                        }
                                                    }}
                                                    placeholder="https://example.com"
                                                    className="w-full"
                                                    required
                                                />
                                                <p className="mt-1 text-xs text-muted-foreground">
                                                    Enter your website URL (http:// or https:// will be added automatically if missing)
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Phone Number *
                                                </label>
                                                <Input
                                                    type="tel"
                                                    value={newCompanyFormData.phone || ''}
                                                    onChange={(e) => {
                                                        // Extract only digits from input
                                                        const digits = e.target.value.replace(/\D/g, '');
                                                        // Format using AsYouType (defaults to US format)
                                                        const formatter = new AsYouType('US');
                                                        const formatted = formatter.input(digits);
                                                        setNewCompanyFormData({ ...newCompanyFormData, phone: formatted });
                                                    }}
                                                    placeholder="(555) 123-4567"
                                                    className="w-full"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Company Size *
                                                </label>
                                                <select
                                                    value={newCompanyFormData.size || ''}
                                                    onChange={(e) => setNewCompanyFormData({ ...newCompanyFormData, size: e.target.value })}
                                                    required
                                                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="">Select size</option>
                                                    <option value="MICRO">Micro</option>
                                                    <option value="SMALL">Small</option>
                                                    <option value="MEDIUM">Medium</option>
                                                    <option value="LARGE">Large</option>
                                                    <option value="ENTERPRISE">Enterprise</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Industry *
                                                </label>
                                                <select
                                                    value={newCompanyFormData.industry || ''}
                                                    onChange={(e) => setNewCompanyFormData({ ...newCompanyFormData, industry: e.target.value })}
                                                    required
                                                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="">Select industry</option>
                                                    {industries.map((ind) => (
                                                        <option key={ind.id} value={ind.indname}>
                                                            {ind.indname}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    MOQ (Minimum Order Quantity)
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={newCompanyFormData.MOQ || ''}
                                                    onChange={(e) => setNewCompanyFormData({ ...newCompanyFormData, MOQ: e.target.value })}
                                                    placeholder="e.g., 100 units"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-3 justify-end pt-4 border-t border-border">
                                            <Button
                                                type="button"
                                                onClick={handleCancelCreateCompany}
                                                variant="outline"
                                                disabled={savingCompany}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={savingCompany}
                                            >
                                                {savingCompany ? 'Creating...' : 'Create Company'}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        ) : companies.length === 0 ? (
                            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                                <p className="text-muted-foreground mb-4">No companies found. Add your first company to get started.</p>
                                {canManageCompanies(userType) && (
                                    <Button
                                        type="button"
                                        onClick={handleStartCreateCompany}
                                        variant="default"
                                    >
                                        + Add Company
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {companies.map((company) => (
                                    <div
                                        key={company.id}
                                        className="bg-card rounded-lg shadow-sm border border-border overflow-hidden"
                                    >
                                        {/* Company Header */}
                                        <div className="px-6 py-5 bg-gradient-to-r from-brand-orange-muted to-brand-purple/10 border-b border-border">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-xl font-semibold text-foreground">
                                                    Company Information
                                                </h3>
                                                {editingCompanyId !== company.id && (
                                                    <Button
                                                        type="button"
                                                        onClick={() => handleEditCompany(company)}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                            </div>

                                            {editingCompanyId === company.id ? (
                                                <form onSubmit={(e) => handleSaveCompany(e, company.id)} className="space-y-4">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Legal Name *
                                                            </label>
                                                            <Input
                                                                type="text"
                                                                value={companyFormData.legal_name || ''}
                                                                onChange={(e) => setCompanyFormData({ ...companyFormData, legal_name: e.target.value })}
                                                                required
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Website
                                                            </label>
                                                            <Input
                                                                type="url"
                                                                value={companyFormData.website || ''}
                                                                onChange={(e) => setCompanyFormData({ ...companyFormData, website: e.target.value })}
                                                                placeholder="https://example.com"
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Phone
                                                            </label>
                                                            <Input
                                                                type="tel"
                                                                value={companyFormData.phone || ''}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    const digits = value.replace(/\D/g, '');
                                                                    if (digits) {
                                                                        const formatter = new AsYouType('US');
                                                                        const formatted = formatter.input(digits);
                                                                        setCompanyFormData({ ...companyFormData, phone: formatted });
                                                                    } else {
                                                                        setCompanyFormData({ ...companyFormData, phone: '' });
                                                                    }
                                                                }}
                                                                placeholder="(555) 123-4567"
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Company Size *
                                                            </label>
                                                            <select
                                                                value={companyFormData.size || ''}
                                                                onChange={(e) => setCompanyFormData({ ...companyFormData, size: e.target.value })}
                                                                required
                                                                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                            >
                                                                <option value="">Select size</option>
                                                                <option value="MICRO">Micro</option>
                                                                <option value="SMALL">Small</option>
                                                                <option value="MEDIUM">Medium</option>
                                                                <option value="LARGE">Large</option>
                                                                <option value="ENTERPRISE">Enterprise</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Industry *
                                                            </label>
                                                            <select
                                                                value={companyFormData.industry || ''}
                                                                onChange={(e) => setCompanyFormData({ ...companyFormData, industry: e.target.value })}
                                                                required
                                                                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                            >
                                                                <option value="">Select industry</option>
                                                                {industries.map((ind) => (
                                                                    <option key={ind.id} value={ind.indname}>
                                                                        {ind.indname}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                MOQ (Minimum Order Quantity)
                                                            </label>
                                                            <Input
                                                                type="text"
                                                                value={companyFormData.MOQ || ''}
                                                                onChange={(e) => setCompanyFormData({ ...companyFormData, MOQ: e.target.value })}
                                                                placeholder="e.g., 100 units"
                                                                className="w-full"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-3 justify-end pt-4 border-t border-border">
                                                        <Button
                                                            type="button"
                                                            onClick={handleCancelEditCompany}
                                                            variant="outline"
                                                            disabled={savingCompany}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            type="submit"
                                                            disabled={savingCompany}
                                                        >
                                                            {savingCompany ? 'Saving...' : 'Save Changes'}
                                                        </Button>
                                                    </div>
                                                </form>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                                    <div className="flex items-start">
                                                        <span className="text-muted-foreground mr-2">🏢</span>
                                                        <div>
                                                            <div className="text-muted-foreground">Legal Name</div>
                                                            <div className="text-foreground font-medium">{company.legalname || 'Not set'}</div>
                                                        </div>
                                                    </div>
                                                    {company.website && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">🌐</span>
                                                            <div>
                                                                <div className="text-muted-foreground">Website</div>
                                                                <a
                                                                    href={company.website}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-brand-orange hover:underline break-all"
                                                                >
                                                                    {company.website}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {company.phone && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">📞</span>
                                                            <div>
                                                                <div className="text-muted-foreground">Phone</div>
                                                                <div className="text-foreground">{formatPhoneNumber(company.phone)}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {company.size && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">📊</span>
                                                            <div>
                                                                <div className="text-muted-foreground">Size</div>
                                                                <div className="text-foreground">{company.size}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {company.industry && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">🏭</span>
                                                            <div>
                                                                <div className="text-muted-foreground">Industry</div>
                                                                <div className="text-foreground">{company.industry}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {company.MOQ && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">📦</span>
                                                            <div>
                                                                <div className="text-muted-foreground">MOQ</div>
                                                                <div className="text-foreground">{company.MOQ}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {company.manager_role && (
                                                        <div className="flex items-start">
                                                            <span className="text-muted-foreground mr-2">👤</span>
                                                            <div>
                                                                <div className="text-muted-foreground">Your Role</div>
                                                                <div className="text-foreground">
                                                                    {company.manager_role}
                                                                    {company.manager_subrole && ` - ${company.manager_subrole}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Locations */}
                                        <div className="px-6 py-5">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="text-lg font-semibold text-foreground flex items-center">
                                                    <span className="mr-2">📍</span>
                                                    Locations {company.location && company.location.length > 0 ? `(${company.location.length})` : ''}
                                                </h4>
                                                {creatingLocationForCompanyId !== company.id && (
                                                    <Button
                                                        type="button"
                                                        onClick={() => handleStartCreateLocation(company.id)}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        + Add Location
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Create Location Form */}
                                            {creatingLocationForCompanyId === company.id && (
                                                <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
                                                    <h5 className="text-sm font-semibold text-foreground mb-4">Add New Location</h5>
                                                    <div className="space-y-4">
                                                        {/* Address Fields Group */}
                                                        <div className="p-4 bg-background rounded-lg border border-border">
                                                            <h6 className="text-xs font-semibold text-foreground mb-4 uppercase tracking-wide">Address Information</h6>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                        Street Address *
                                                                    </label>
                                                                    <Input
                                                                        type="text"
                                                                        value={newLocationFormData.address || ''}
                                                                        onChange={(e) => setNewLocationFormData({ ...newLocationFormData, address: e.target.value })}
                                                                        placeholder="123 Main Street"
                                                                        required
                                                                        className="w-full"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                        City *
                                                                    </label>
                                                                    <Input
                                                                        type="text"
                                                                        value={newLocationFormData.city || ''}
                                                                        onChange={(e) => setNewLocationFormData({ ...newLocationFormData, city: e.target.value })}
                                                                        placeholder="New York"
                                                                        required
                                                                        className="w-full"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                        Country *
                                                                    </label>
                                                                    <select
                                                                        value={newLocationFormData.country_code || ''}
                                                                        onChange={(e) => {
                                                                            const countryCode = e.target.value;
                                                                            setNewLocationFormData({ ...newLocationFormData, country_code: countryCode, state: '' });
                                                                            if (countryCode) {
                                                                                loadStates(countryCode);
                                                                            } else {
                                                                                setStates([]);
                                                                            }
                                                                        }}
                                                                        required
                                                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                    >
                                                                        <option value="">Select a country</option>
                                                                        {countries.map((country) => (
                                                                            <option key={country.country_code} value={country.country_code}>
                                                                                {country.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                {states.length > 0 ? (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                            State/Province
                                                                        </label>
                                                                        <select
                                                                            value={newLocationFormData.state || ''}
                                                                            onChange={(e) => setNewLocationFormData({ ...newLocationFormData, state: e.target.value })}
                                                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                        >
                                                                            <option value="">Select a state/province</option>
                                                                            {states.map((state) => (
                                                                                <option key={state.state_code} value={state.state_code}>
                                                                                    {state.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                ) : newLocationFormData.country_code ? (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                            State/Province
                                                                        </label>
                                                                        <Input
                                                                            type="text"
                                                                            value={newLocationFormData.state || ''}
                                                                            onChange={(e) => setNewLocationFormData({ ...newLocationFormData, state: e.target.value })}
                                                                            className="w-full"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                            State/Province
                                                                        </label>
                                                                        <Input
                                                                            type="text"
                                                                            value={newLocationFormData.state || ''}
                                                                            onChange={(e) => setNewLocationFormData({ ...newLocationFormData, state: e.target.value })}
                                                                            placeholder="Select country first"
                                                                            disabled
                                                                            className="w-full"
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                        Postal/ZIP Code
                                                                    </label>
                                                                    <Input
                                                                        type="text"
                                                                        value={newLocationFormData.postal_code || ''}
                                                                        onChange={(e) => setNewLocationFormData({ ...newLocationFormData, postal_code: e.target.value })}
                                                                        placeholder="10001"
                                                                        className="w-full"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Business Function Field */}
                                                        <div>
                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                Business Function
                                                            </label>
                                                            <Input
                                                                type="text"
                                                                value={newLocationFormData.businessfunction || ''}
                                                                onChange={(e) => setNewLocationFormData({ ...newLocationFormData, businessfunction: e.target.value })}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div className="flex gap-3 justify-end pt-2 border-t border-border">
                                                            <Button
                                                                type="button"
                                                                onClick={handleCancelCreateLocation}
                                                                variant="outline"
                                                                disabled={savingLocation}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                onClick={() => handleCreateLocation(company.id)}
                                                                disabled={savingLocation}
                                                            >
                                                                {savingLocation ? 'Creating...' : 'Create Location'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {company.location && company.location.length > 0 ? (
                                                <div>
                                                    <div className="space-y-3">
                                                        {company.location.map((location) => (
                                                            <div
                                                                key={location.id}
                                                                className="p-4 bg-muted rounded-lg border border-border"
                                                            >
                                                                {editingLocationId === location.id ? (
                                                                    <div className="space-y-4">
                                                                        {/* Address Fields Group */}
                                                                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                                                                            <h5 className="text-sm font-semibold text-foreground mb-4">Address Information</h5>
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                                        Address *
                                                                                    </label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={locationFormData.address || ''}
                                                                                        onChange={(e) => setLocationFormData({ ...locationFormData, address: e.target.value })}
                                                                                        required
                                                                                        className="w-full"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                                        City *
                                                                                    </label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={locationFormData.city || ''}
                                                                                        onChange={(e) => setLocationFormData({ ...locationFormData, city: e.target.value })}
                                                                                        required
                                                                                        className="w-full"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                                        Country *
                                                                                    </label>
                                                                                    <select
                                                                                        value={locationFormData.country_code || ''}
                                                                                        onChange={(e) => {
                                                                                            const countryCode = e.target.value;
                                                                                            setLocationFormData({ ...locationFormData, country_code: countryCode, state: '' });
                                                                                            if (countryCode) {
                                                                                                loadStates(countryCode);
                                                                                            } else {
                                                                                                setStates([]);
                                                                                            }
                                                                                        }}
                                                                                        required
                                                                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                                    >
                                                                                        <option value="">Select Country</option>
                                                                                        {countries.map((country) => (
                                                                                            <option key={country.country_code} value={country.country_code}>
                                                                                                {country.name}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                                {states.length > 0 ? (
                                                                                    <div>
                                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                                            State/Province
                                                                                        </label>
                                                                                        <select
                                                                                            value={locationFormData.state || ''}
                                                                                            onChange={(e) => setLocationFormData({ ...locationFormData, state: e.target.value })}
                                                                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                                        >
                                                                                            <option value="">Select State</option>
                                                                                            {states.map((state) => (
                                                                                                <option key={state.state_code} value={state.state_code}>
                                                                                                    {state.name}
                                                                                                </option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                ) : locationFormData.country_code ? (
                                                                                    <div>
                                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                                            State/Province
                                                                                        </label>
                                                                                        <Input
                                                                                            type="text"
                                                                                            value={locationFormData.state || ''}
                                                                                            onChange={(e) => setLocationFormData({ ...locationFormData, state: e.target.value })}
                                                                                            className="w-full"
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div>
                                                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                                                            State/Province
                                                                                        </label>
                                                                                        <Input
                                                                                            type="text"
                                                                                            value={locationFormData.state || ''}
                                                                                            onChange={(e) => setLocationFormData({ ...locationFormData, state: e.target.value })}
                                                                                            className="w-full"
                                                                                            placeholder="Select country first"
                                                                                            disabled
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                                                        Postal Code
                                                                                    </label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={locationFormData.postal_code || ''}
                                                                                        onChange={(e) => setLocationFormData({ ...locationFormData, postal_code: e.target.value })}
                                                                                        className="w-full"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Business Function Field */}
                                                                        <div>
                                                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                                                Business Function
                                                                            </label>
                                                                            <Input
                                                                                type="text"
                                                                                value={locationFormData.businessfunction || ''}
                                                                                onChange={(e) => setLocationFormData({ ...locationFormData, businessfunction: e.target.value })}
                                                                                className="w-full"
                                                                            />
                                                                        </div>
                                                                        <div className="flex gap-3 justify-end pt-2 border-t border-border">
                                                                            <Button
                                                                                type="button"
                                                                                onClick={handleCancelEditLocation}
                                                                                variant="outline"
                                                                                disabled={savingLocation}
                                                                            >
                                                                                Cancel
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                onClick={() => handleSaveLocation(location.id, company.id)}
                                                                                disabled={savingLocation}
                                                                            >
                                                                                {savingLocation ? 'Saving...' : 'Save Location'}
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        {/* Address Section */}
                                                                        <div className="mb-4">
                                                                            <div className="flex justify-between items-center mb-2">
                                                                                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                                                                                    Address
                                                                                </div>
                                                                                <Button
                                                                                    type="button"
                                                                                    onClick={() => handleEditLocation(location)}
                                                                                    variant="outline"
                                                                                    size="sm"
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                                                                {location.address && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Address</span>
                                                                                        <div className="text-foreground mt-1">{location.address}</div>
                                                                                    </div>
                                                                                )}
                                                                                {location.city && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">City</span>
                                                                                        <div className="text-foreground mt-1">{location.city}</div>
                                                                                    </div>
                                                                                )}
                                                                                {location.state && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">State</span>
                                                                                        <div className="text-foreground mt-1">{location.state}</div>
                                                                                    </div>
                                                                                )}
                                                                                {location.postal_code && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Postal Code</span>
                                                                                        <div className="text-foreground mt-1">{location.postal_code}</div>
                                                                                    </div>
                                                                                )}
                                                                                {location.country_code && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Country</span>
                                                                                        <div className="text-foreground mt-1">{location.country_code}</div>
                                                                                    </div>
                                                                                )}
                                                                                {location.businessfunction && (
                                                                                    <div>
                                                                                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Business Function</span>
                                                                                        <div className="text-foreground mt-1">{location.businessfunction}</div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Products Section */}
                                                                        <div className="mt-4 pt-4 border-t border-border">
                                                                            <div className="flex justify-between items-center mb-2">
                                                                                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                                                                                    Products
                                                                                </div>
                                                                                <Button
                                                                                    type="button"
                                                                                    onClick={() => handleEditProducts(location)}
                                                                                    variant="outline"
                                                                                    size="sm"
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                            </div>
                                                                            {location.locationProducts && location.locationProducts.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {location.locationProducts.map((lp, idx) => (
                                                                                        <span
                                                                                            key={idx}
                                                                                            className="inline-block px-3 py-1 text-xs bg-background text-foreground rounded border border-border"
                                                                                        >
                                                                                            {lp.product?.itemname || lp.product?.categoryname || 'Product'}
                                                                                            {lp.buying && (
                                                                                                <span className="ml-2 text-muted-foreground">(Buying)</span>
                                                                                            )}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-sm text-muted-foreground">No products selected</p>
                                                                            )}
                                                                        </div>

                                                                        {/* Services Section */}
                                                                        <div className="mt-4 pt-4 border-t border-border">
                                                                            <div className="flex justify-between items-center mb-2">
                                                                                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                                                                                    Services
                                                                                </div>
                                                                                <Button
                                                                                    type="button"
                                                                                    onClick={() => handleEditServices(location)}
                                                                                    variant="outline"
                                                                                    size="sm"
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                            </div>
                                                                            {location.locationServices && location.locationServices.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {location.locationServices.map((ls, idx) => (
                                                                                        <span
                                                                                            key={idx}
                                                                                            className="inline-block px-3 py-1 text-xs bg-background text-foreground rounded border border-border"
                                                                                        >
                                                                                            {ls.service?.itemname || ls.service?.categoryname || 'Service'}
                                                                                            {ls.buying && (
                                                                                                <span className="ml-2 text-muted-foreground">(Buying)</span>
                                                                                            )}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-sm text-muted-foreground">No services selected</p>
                                                                            )}
                                                                        </div>

                                                                        {/* Edit Products Modal */}
                                                                        {editingProductsLocationId === location.id && (
                                                                            <div className="mt-4 pt-4 border-t border-border">
                                                                                <div className="mb-3">
                                                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Edit Products</h5>
                                                                                    <p className="text-xs text-muted-foreground mb-3">
                                                                                        Select products for this location
                                                                                    </p>
                                                                                    <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2">
                                                                                        {products.map((product) => {
                                                                                            const isSelected = location.locationProducts?.some(
                                                                                                lp => lp.product?.id === product.id
                                                                                            );
                                                                                            return (
                                                                                                <button
                                                                                                    key={product.id}
                                                                                                    type="button"
                                                                                                    onClick={(e) => {
                                                                                                        e.preventDefault();
                                                                                                        e.stopPropagation();
                                                                                                        toggleProductForLocation(
                                                                                                            e,
                                                                                                            location.id,
                                                                                                            product.id,
                                                                                                            !isSelected
                                                                                                        );
                                                                                                        return false;
                                                                                                    }}
                                                                                                    className={cn(
                                                                                                        'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                                                                        isSelected
                                                                                                            ? 'border-brand-orange bg-brand-orange-muted'
                                                                                                            : 'border-gray-200 hover:border-gray-300'
                                                                                                    )}
                                                                                                >
                                                                                                    <div className="font-semibold">
                                                                                                        {product.itemname || product.categoryname || 'Product'}
                                                                                                    </div>
                                                                                                    {product.categoryname && product.itemname && (
                                                                                                        <div className="text-sm text-muted-foreground">
                                                                                                            {product.categoryname}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </button>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex gap-3 justify-end pt-2 border-t border-border">
                                                                                    <Button
                                                                                        type="button"
                                                                                        onClick={handleCancelEditProducts}
                                                                                        variant="outline"
                                                                                    >
                                                                                        Done
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Edit Services Modal */}
                                                                        {editingServicesLocationId === location.id && (
                                                                            <div className="mt-4 pt-4 border-t border-border">
                                                                                <div className="mb-3">
                                                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Edit Services</h5>
                                                                                    <p className="text-xs text-muted-foreground mb-3">
                                                                                        Select services for this location
                                                                                    </p>
                                                                                    <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2">
                                                                                        {services.map((service) => {
                                                                                            const isSelected = location.locationServices?.some(
                                                                                                ls => ls.service?.id === service.id
                                                                                            );
                                                                                            return (
                                                                                                <button
                                                                                                    key={service.id}
                                                                                                    type="button"
                                                                                                    onClick={(e) => {
                                                                                                        e.preventDefault();
                                                                                                        e.stopPropagation();
                                                                                                        toggleServiceForLocation(
                                                                                                            e,
                                                                                                            location.id,
                                                                                                            service.id,
                                                                                                            !isSelected
                                                                                                        );
                                                                                                        return false;
                                                                                                    }}
                                                                                                    className={cn(
                                                                                                        'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                                                                        isSelected
                                                                                                            ? 'border-brand-orange bg-brand-orange-muted'
                                                                                                            : 'border-gray-200 hover:border-gray-300'
                                                                                                    )}
                                                                                                >
                                                                                                    <div className="font-semibold">
                                                                                                        {service.itemname || service.categoryname || 'Service'}
                                                                                                    </div>
                                                                                                    {service.categoryname && service.itemname && (
                                                                                                        <div className="text-sm text-muted-foreground">
                                                                                                            {service.categoryname}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </button>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex gap-3 justify-end pt-2 border-t border-border">
                                                                                    <Button
                                                                                        type="button"
                                                                                        onClick={handleCancelEditServices}
                                                                                        variant="outline"
                                                                                    >
                                                                                        Done
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 text-muted-foreground">
                                                    <span className="text-2xl mb-2 block">📍</span>
                                                    <p>No locations found for this company.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Danger Zone - Delete Profile */}
                {activeTab === 'profile' && (
                    <div className="bg-card rounded-lg shadow-sm border border-red-200">
                        <div className="px-6 py-5 border-b border-red-200 flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-red-700">Delete Profile</h2>
                        </div>
                        <div className="p-6 space-y-3">
                            <p className="text-sm text-red-700">
                                Deleting your profile will deactivate your account and remove your profile from the
                                directory. This action cannot be undone.
                            </p>
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={deletingProfile || loading}
                                onClick={handleDeleteProfile}
                            >
                                {deletingProfile ? 'Deleting profile...' : 'Delete profile'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Buyer Requirements Section */}
                {activeTab === 'requirements' && (
                    <div>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-foreground mb-2">Requirements</h2>
                            <p className="text-muted-foreground">
                                These requirements are created automatically based on the products and services you&apos;re buying.
                                Use this section to provide additional details about what you&apos;re looking for to help us match you with the best suppliers.
                            </p>
                        </div>

                        {loadingRequirements ? (
                            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                                <div className="text-muted-foreground">Loading buyer requirements...</div>
                            </div>
                        ) : buyerRequirements.length === 0 ? (
                            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
                                <p className="text-muted-foreground">
                                    No requirements found yet. Add products or services you are buying to your company locations,
                                    and they will appear here automatically.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {buyerRequirements.map((req) => (
                                    <div
                                        key={req.id}
                                        className="bg-card rounded-lg shadow-sm border border-border p-6"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                                            <div>
                                                <h3 className="text-lg font-semibold text-foreground">
                                                    {req.category}
                                                </h3>
                                                {req.companyName && (
                                                    <p className="text-sm text-muted-foreground">
                                                        Company: {req.companyName}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-muted-foreground">Priority</span>
                                                <select
                                                    value={req.priority || 'MEDIUM'}
                                                    onChange={(e) =>
                                                        handleRequirementChange(req.id, 'priority', e.target.value)
                                                    }
                                                    className="px-3 py-1 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="LOW">Low</option>
                                                    <option value="MEDIUM">Medium</option>
                                                    <option value="HIGH">High</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Quantity / Volume
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={req.quantity || ''}
                                                    onChange={(e) =>
                                                        handleRequirementChange(req.id, 'quantity', e.target.value)
                                                    }
                                                    placeholder="e.g., 10,000 units per month"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Timeline / Urgency
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={req.timeline || ''}
                                                    onChange={(e) =>
                                                        handleRequirementChange(req.id, 'timeline', e.target.value)
                                                    }
                                                    placeholder="e.g., Within 3 months"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Budget Range
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={req.budget || ''}
                                                    onChange={(e) =>
                                                        handleRequirementChange(req.id, 'budget', e.target.value)
                                                    }
                                                    placeholder="e.g., $50k - $100k annually"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Geography / Location Preferences
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={req.geography || ''}
                                                    onChange={(e) =>
                                                        handleRequirementChange(req.id, 'geography', e.target.value)
                                                    }
                                                    placeholder="e.g., North America, Europe"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Additional Notes / Specifications
                                            </label>
                                            <textarea
                                                value={req.notes || ''}
                                                onChange={(e) =>
                                                    handleRequirementChange(req.id, 'notes', e.target.value)
                                                }
                                                rows={3}
                                                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                                placeholder="Share any specific requirements, technical specs, certifications, or preferences."
                                            />
                                        </div>

                                        <div className="flex justify-end mt-4">
                                            <Button
                                                type="button"
                                                onClick={() => handleSaveRequirement(req.id)}
                                                disabled={savingRequirementId === req.id}
                                            >
                                                {savingRequirementId === req.id ? 'Saving...' : 'Save Details'}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
}

export default Profile;

